/**
 * Production action primitives — Button (primary / secondary / ghost) and Actions, which
 * maps a schema `Action[]` straight into a row of buttons (target → href/tel/mailto/anchor,
 * emphasis → variant). Sections never hardcode buttons; they pass `content.actions`.
 */
import type { CSSProperties, ReactNode } from 'react'
import type { Action } from '../../schema'
import type { Align, ButtonVariant } from './types'
import { cx } from './utils'

interface ButtonProps {
  readonly variant?: ButtonVariant
  readonly size?: 'md' | 'lg'
  readonly href?: string
  readonly type?: 'button' | 'submit'
  readonly onClick?: () => void
  readonly className?: string
  readonly children: ReactNode
}

export function Button({ variant = 'primary', size = 'md', href, type = 'button', onClick, className, children }: ButtonProps) {
  const cls = cx('sbp-btn', `sbp-btn--${variant}`, size === 'lg' && 'sbp-btn--lg', className)
  if (href !== undefined) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    )
  }
  return (
    <button type={type} onClick={onClick} className={cls}>
      {children}
    </button>
  )
}

/** Resolve an Action's href for its target. */
function hrefFor(action: Action): string {
  switch (action.target) {
    case 'phone':
      return `tel:${action.value ?? ''}`
    case 'email':
      return `mailto:${action.value ?? ''}`
    case 'anchor':
      return `#${action.value ?? ''}`
    default:
      return action.value ?? '#'
  }
}

interface ActionsProps {
  readonly actions?: readonly Action[]
  readonly align?: Align
  readonly size?: 'md' | 'lg'
  readonly className?: string
}

export function Actions({ actions, align, size, className }: ActionsProps) {
  if (!actions || actions.length === 0) return null
  const style: CSSProperties | undefined = align
    ? { justifyContent: align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : 'center' }
    : undefined
  return (
    <div className={cx('sbp-actions', className)} style={style}>
      {actions.map((action) => (
        <Button key={action.id} variant={action.emphasis} size={size} href={hrefFor(action)}>
          {action.label}
        </Button>
      ))}
    </div>
  )
}
